import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api, { formatApiError } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const TeamAttendanceReport = () => {
  const [members, setMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [records, setRecords] = useState([]);
  const [workLogs, setWorkLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/users').then(r => {
      const teamRoles = ['admin', 'manager', 'editor_shooter', 'photographer', 'videographer', 'social_media', 'sales_person'];
      const list = (r.data || []).filter(u => (u.roles || [u.role]).some(role => teamRoles.includes(role)));
      setMembers(list);
      if (list.length && !selectedUserId) setSelectedUserId(list[0].id);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAttendance = useCallback(async () => {
    if (!selectedUserId) return;
    setLoading(true);
    try {
      const startStr = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endStr = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
      const [attRes, logRes] = await Promise.all([
        api.get('/attendance', { params: { user_id: selectedUserId, month, year } }),
        api.get('/work-logs', { params: { user_id: selectedUserId, start: startStr, end: endStr } }),
      ]);
      setRecords(attRes.data || []);
      setWorkLogs(logRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [selectedUserId, month, year]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  // Build a map keyed by yyyy-mm-dd
  const dayMap = useMemo(() => {
    const m = {};
    for (const r of records) {
      if (!r.date) continue;
      const d = new Date(r.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      m[key] = r;
    }
    return m;
  }, [records]);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDay = new Date(year, month - 1, 1).getDay();
  const today = new Date();
  const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  const stats = useMemo(() => {
    let present = 0, leave = 0, shoot = 0;
    Object.values(dayMap).forEach(r => {
      const s = (r.status || '').toLowerCase();
      if (s === 'present' || s === 'half_day') present++;
      else if (s === 'leave' || s === 'sick' || s === 'casual') leave++;
      else if (s === 'shoot_day') shoot++;
    });
    return { present, leave, shoot, total: Object.keys(dayMap).length };
  }, [dayMap]);

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m > 12) { m = 1; y++; }
    if (m < 1) { m = 12; y--; }
    setMonth(m); setYear(y);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push({ blank: true });
  for (let d = 1; d <= daysInMonth; d++) {
    const key = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ day: d, rec: dayMap[key], key, isToday: key === todayKey });
  }

  const statusStyle = (s) => {
    s = (s || '').toLowerCase();
    if (s === 'present') return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
    if (s === 'half_day') return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
    if (s === 'leave' || s === 'casual' || s === 'sick') return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    if (s === 'shoot_day') return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
    return 'bg-white/[0.04] text-white/40 border-white/10';
  };

  return (
    <div className="space-y-4" data-testid="attendance-report">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={selectedUserId} onValueChange={setSelectedUserId}>
          <SelectTrigger className="w-[260px] bg-[#0A0A0A] border-white/10" data-testid="select-team-member">
            <SelectValue placeholder="Select team member" />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10 max-h-[300px]">
            {members.map(m => (
              <SelectItem key={m.id} value={m.id}>
                {m.name} <span className="text-white/40 text-xs">({m.role})</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => changeMonth(-1)} data-testid="prev-month">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-sm font-medium min-w-[120px] text-center">{MONTHS[month - 1]} {year}</div>
          <Button size="sm" variant="outline" onClick={() => changeMonth(1)} data-testid="next-month">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <Button size="sm" variant="outline" onClick={fetchAttendance} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Card className="bg-[#121212] border-white/10"><CardContent className="p-3">
          <p className="text-xs text-zinc-500">Present</p>
          <p className="text-2xl font-semibold text-emerald-400">{stats.present}</p>
        </CardContent></Card>
        <Card className="bg-[#121212] border-white/10"><CardContent className="p-3">
          <p className="text-xs text-zinc-500">Leaves</p>
          <p className="text-2xl font-semibold text-blue-400">{stats.leave}</p>
        </CardContent></Card>
        <Card className="bg-[#121212] border-white/10"><CardContent className="p-3">
          <p className="text-xs text-zinc-500">Shoot Days</p>
          <p className="text-2xl font-semibold text-purple-400">{stats.shoot}</p>
        </CardContent></Card>
        <Card className="bg-[#121212] border-white/10"><CardContent className="p-3">
          <p className="text-xs text-zinc-500">Days Logged</p>
          <p className="text-2xl font-semibold text-white">{stats.total}</p>
        </CardContent></Card>
      </div>

      {/* Calendar */}
      <Card className="bg-[#121212] border-white/10">
        <CardContent className="p-4">
          <div className="grid grid-cols-7 gap-1 text-center text-[10px] text-zinc-500 uppercase tracking-wider mb-1">
            {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((c, i) => c.blank ? (
              <div key={`b${i}`} />
            ) : (
              <div key={c.key}
                className={`aspect-square rounded border p-1.5 text-xs flex flex-col justify-between ${statusStyle(c.rec?.status)} ${c.isToday ? 'ring-1 ring-[#F5A623]' : ''}`}
                title={c.rec ? `${c.rec.status} ${c.rec.check_in_time ? '· in: ' + new Date(c.rec.check_in_time).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'}) : ''}` : ''}>
                <div className="font-medium">{c.day}</div>
                {c.rec && (
                  <div className="text-[9px] opacity-80 leading-tight">
                    {(c.rec.status || '').replace('_', ' ')}
                    {c.rec.check_in_time && (
                      <div className="flex items-center gap-0.5"><Clock className="w-2 h-2" />{new Date(c.rec.check_in_time).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Work Logs for selected member */}
      <Card className="bg-[#121212] border-white/10" data-testid="member-work-logs">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-2 text-sm font-medium text-white/80">
            <CalIcon className="w-4 h-4 text-[#F5A623]" /> What they worked on this month
            <span className="text-xs text-zinc-500">({workLogs.length} {workLogs.length === 1 ? 'entry' : 'entries'})</span>
          </div>
          {workLogs.length === 0 ? (
            <p className="text-xs text-zinc-500 py-4 text-center">No work logs for this period.</p>
          ) : (
            <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
              {workLogs.map(l => (
                <div key={l.id} className="flex items-start gap-3 text-xs bg-white/[0.02] rounded px-3 py-2">
                  <span className="text-white/80 w-20 shrink-0">{new Date(l.log_date + 'T00:00:00').toLocaleDateString(undefined, { day:'2-digit', month:'short' })}</span>
                  <span className="flex-1 text-white/90 whitespace-pre-wrap break-words">{l.summary}</span>
                  {l.hours != null && <span className="text-[#F5A623] shrink-0">{l.hours}h</span>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail list */}
      {records.length > 0 && (
        <Card className="bg-[#121212] border-white/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2 text-sm font-medium text-white/80">
              <CalIcon className="w-4 h-4" /> Daily log
            </div>
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {records
                .slice()
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((r, i) => (
                  <div key={r.id || i} className="flex items-center gap-3 text-xs bg-white/[0.02] rounded px-3 py-2">
                    <span className="text-white/80 w-24">{new Date(r.date).toLocaleDateString(undefined, { day:'2-digit', month:'short' })}</span>
                    <span className={`px-2 py-0.5 rounded ${statusStyle(r.status)}`}>{(r.status || '').replace('_', ' ')}</span>
                    {r.check_in_time && <span className="text-white/60">In: {new Date(r.check_in_time).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>}
                    {r.check_out_time && <span className="text-white/60">Out: {new Date(r.check_out_time).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>}
                    {r.notes && <span className="text-white/40 truncate">{r.notes}</span>}
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default TeamAttendanceReport;
